<div class="billing-overview">
    <header class="billing-header">
        <div><span class="eyebrow">REPORTS / BILLING</span><h1>{{org?.name || 'Billing overview'}}</h1><p>Review device growth and license activity for your organization.</p></div>
        <a mat-stroked-button [routerLink]="['/layout/report/overview/'+orgId]">&#8592; Report overview</a>
    </header>
    <section class="billing-panel filter-panel" aria-labelledby="period-title">
        <div><h2 id="period-title">Reporting period</h2><p>Select a date range to update monthly devices and license activity.</p></div>
        <form class="date-filters" (ngSubmit)="loadData()">
            <mat-form-field appearance="outline">
                <mat-label>Start date</mat-label>
                <input matInput [matDatepicker]="startPicker" [min]="minDate" [max]="maxDate" [formControl]="startMonth" required>
                <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
                <mat-error>Enter a valid start date.</mat-error>
            </mat-form-field>
            <mat-form-field appearance="outline">
                <mat-label>End date</mat-label>
                <input matInput [matDatepicker]="endPicker" [min]="startMonth.value || minDate" [max]="maxDate" [formControl]="endMonth" required>
                <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
                <mat-error>Choose an end date on or after the start date.</mat-error>
            </mat-form-field>
            <button mat-flat-button class="apply-button" type="submit" [disabled]="loading || invalidDateRange">{{loading ? 'Loading...' : 'Apply dates'}}</button>
        </form>
        <p class="date-warning" role="status" *ngIf="!loading && invalidDateRange">Choose a valid date range within the last three years.</p>
    </section>
    <div class="report-error" role="alert" *ngIf="loadError"><span>{{loadError}}</span><button mat-button (click)="loadData()" [disabled]="invalidDateRange">Retry</button></div>
    <div class="loading-notice" role="status" *ngIf="loading">Loading billing report...</div>
    <p class="applied-period" *ngIf="hasReport">{{loading ? 'Previous report' : 'Showing report'}}: <strong>{{appliedRange}}</strong></p>
    <div class="billing-metrics" [attr.aria-busy]="loading">
        <article><span>Registered devices</span><strong>{{hasReport ? (deviceArrayAll.length | number) : '-'}}</strong><small>Current organization inventory</small></article>
        <article><span>License activity</span><strong>{{hasReport ? (licenseHistorySource.data.length | number) : '-'}}</strong><small>Events in the reporting period</small></article>
        <article><span>Months covered</span><strong>{{hasReport ? monthlyDeviceCntSource.data.length : '-'}}</strong><small>Monthly device breakdown</small></article>
    </div>
    <section class="billing-panel monthly-panel" aria-labelledby="monthly-title" [attr.aria-busy]="loading">
        <div class="section-heading"><div><h2 id="monthly-title">Monthly devices</h2><p>Devices created each month in the selected period.</p></div></div>
        <div class="monthly-grid">
            <div class="table-scroll" [hidden]="!hasReport"><table mat-table [dataSource]="monthlyDeviceCntSource" style="width: 100%;">
                        <ng-container matColumnDef="monthsArray">
                            <th mat-header-cell *matHeaderCellDef><b>Month</b></th>
                            <td mat-cell *matCellDef="let element">{{element.month}}</td>
                        </ng-container>

                        <ng-container matColumnDef="monthlyDeviceCount">
                            <th mat-header-cell *matHeaderCellDef><b>Device Count</b></th>
                            <td mat-cell *matCellDef="let element">{{element.count}}</td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="displayedTemplateColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedTemplateColumns"></tr>
                    </table></div>
            <div class="chart-wrapper" [class.chart-pending]="!hasReport"><canvas id="billing-monthly-chart" role="img" aria-label="Monthly devices created. Exact counts are in the adjacent table."></canvas></div>
        </div>
        <p class="empty-state" *ngIf="hasReport && !loading && !deviceArray.length">No devices were created during this period. Try a different date range.</p>
    </section>
    <section class="billing-panel details-panel" aria-label="Billing report details" [attr.aria-busy]="loading">
        <mat-tab-group class="billing-tabs" animationDuration="0ms">
            <mat-tab label="License activity">
                <div class="activity-toolbar">
                    <div><h2>License activity</h2><p>Review license changes and the users who made them.</p></div>
                    <mat-form-field appearance="outline"><mat-label>Search activity</mat-label><input matInput type="search" [value]="licenseSearch" (input)="filterLicenses($event.target.value)" placeholder="Device, user, or license" [disabled]="!hasReport || loading"></mat-form-field>
                </div>
                <div class="table-scroll activity-table" [hidden]="!hasReport"><table mat-table [dataSource]="licenseHistorySource" style="width: 100%;" matSort (matSortChange)="displayLicenseActivity($event)">
                <ng-container matColumnDef="date">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Date</th>
                    <td mat-cell *matCellDef="let element">{{element.date}}</td>
                </ng-container>

                <ng-container matColumnDef="mac">
                    <th mat-header-cell *matHeaderCellDef>Device MAC</th>
                    <td mat-cell *matCellDef="let element">{{element.mac.toUpperCase()}}</td>
                </ng-container>

                <ng-container matColumnDef="devicename">
                    <th mat-header-cell *matHeaderCellDef>Device</th>
                    <td mat-cell *matCellDef="let element">{{element.devicename}}</td>
                </ng-container>

                <ng-container matColumnDef="location">
                    <th mat-header-cell *matHeaderCellDef>Location</th>
                    <td mat-cell *matCellDef="let element">{{element.location}}</td>
                </ng-container>

                <ng-container matColumnDef="mmsp">
                    <th mat-header-cell *matHeaderCellDef>MMSP</th>
                    <td mat-cell *matCellDef="let element">{{element.mmsp}}</td>
                </ng-container>

                <ng-container matColumnDef="msp">
                    <th mat-header-cell *matHeaderCellDef>MSP</th>
                    <td mat-cell *matCellDef="let element">{{element.msp}}</td>
                </ng-container>

                <ng-container matColumnDef="org">
                    <th mat-header-cell *matHeaderCellDef>Organization</th>
                    <td mat-cell *matCellDef="let element">{{element.org}}</td>
                </ng-container>

                <ng-container matColumnDef="old_license">
                    <th mat-header-cell *matHeaderCellDef>Original license</th>
                    <td mat-cell *matCellDef="let element">
                        <span [innerHtml]="element.old_license"></span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="license">
                    <th mat-header-cell *matHeaderCellDef>License change</th>
                    <td mat-cell *matCellDef="let element">
                        <span [innerHtml]="element.license"></span>
                    </td>
                </ng-container>

                <ng-container matColumnDef="user">
                    <th mat-header-cell *matHeaderCellDef>User</th>
                    <td mat-cell *matCellDef="let element">{{element.user}}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedLicenseColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedLicenseColumns"></tr>
            </table></div>
                <p class="empty-state" *ngIf="hasReport && !loading && !licenseHistorySource.filteredData.length">{{licenseSearch ? 'No activity matches your search.' : 'No license changes during this period.'}}</p>
                <mat-paginator [pageSize]="25" [pageSizeOptions]="[10, 25, 50, 100]" showFirstLastButtons aria-label="License activity pages" [hidden]="!hasReport || !licenseHistorySource.filteredData.length"></mat-paginator>
            </mat-tab>
            <mat-tab label="All organizations"><ng-template matTabContent>
                <div class="tab-content" *ngIf="hasReport && allOrgData && deviceArray"><app-org-devices-table [dataSource]="orgDataSource" [deviceSource]="deviceArray" [selfSort]="true" (refresh)="loadData()" [nav]="'billing'"></app-org-devices-table></div>
            </ng-template></mat-tab>
            <mat-tab label="All devices"><ng-template matTabContent>
                <div class="tab-content" *ngIf="hasReport && deviceArrayAll"><app-device-table [dataSource]="deviceArrayAll" [isReportView]="true" [selfSort]="true"></app-device-table></div>
            </ng-template></mat-tab>
        </mat-tab-group>
    </section>
</div>
